/**
 * Fleet state, and the merge policy that keeps it honest.
 *
 * This mirrors api/app/domain/telemetry.py exactly, because the client is the
 * second place the same two bugs can happen. `scalars` overwrite per key.
 * `telemetry` merges per message type into the live cache. Treating the second
 * like the first discarded roughly fourteen of every fifteen readings on a real
 * aircraft in the predecessor, and a simulator emitting four message types hid
 * it for an entire development cycle.
 *
 * There is deliberately no write throttle here. The server coalesces to 10 Hz
 * already, so every frame that reaches this module is one the operator is meant
 * to see. The predecessor throttled store writes to 5 Hz to stop one oversized
 * component re-rendering, which capped how smoothly the map could move and
 * halved position resolution for every consumer to protect a single one. The
 * fix for rendering cost is `subscribeToDrone` and narrow selectors, both
 * below, not throwing telemetry away.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import type {
  ConnectionState,
  Drone,
  DroneAccess,
  DroneDelta,
  DroneId,
  DroneScalars,
  TelemetryCache,
} from '@/types/domain';

export type LinkStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface Alert {
  id: number;
  severity: 'info' | 'warning' | 'critical';
  droneId: DroneId | null;
  message: string;
  at: number;
}

/** How many alerts are retained. Beyond this the oldest is dropped. */
const ALERT_LIMIT = 100;

/**
 * One line the autopilot said, mirroring the server's AutopilotMessage. Kept
 * per drone in a bounded ring so a PreArm failure that scrolled past is still
 * there when the operator opens the panel.
 */
export interface AutopilotMessage {
  droneId: DroneId;
  severity: number;
  level: string;
  text: string;
  timestamp: number;
}

/** Recent autopilot lines retained per drone. Beyond this the oldest is dropped. */
const MESSAGE_LIMIT = 50;

/** A severity at or below this is warning-or-worse: the class that gets a badge. */
const WARNING_SEVERITY = 4;

const EMPTY_MESSAGES: readonly AutopilotMessage[] = [];

/**
 * How long an unmatched delta is held before it is discarded.
 *
 * Telemetry for a drone can beat its roster entry: the broadcaster ticks at
 * 10 Hz and the roster is a REST round trip behind it. The predecessor dropped
 * those frames, so a newly connected aircraft sat at "no data" for as long as
 * the roster took, typically half a second but seconds on a loaded link. Held
 * deltas are applied the moment the drone appears.
 */
const PENDING_TTL_MS = 30_000;

interface PendingDelta {
  scalars: DroneScalars;
  telemetry: TelemetryCache;
  observedAt: number;
  heldSince: number;
}

/** The wire shape of a drone in a snapshot frame. */
export interface DroneSnapshotWire {
  droneId: string;
  callsign: string;
  autopilotSysId: number | null;
  state: ConnectionState;
  scalars: Record<string, unknown>;
  telemetry: TelemetryCache;
  link: string;
  simulated: boolean;
  lastMessageAt: number | null;
  /** Absent from an older server; absence means unsigned, never "assume signed". */
  signing?: boolean;
  /** 'map' for another team's aircraft. Absent from an older server, which sent only your own. */
  access?: DroneAccess;
}

interface DroneStoreState {
  drones: Map<DroneId, Drone>;
  /** Stable array of ids, replaced only when membership changes. */
  ids: DroneId[];
  selectedIds: DroneId[];
  link: LinkStatus;
  alerts: Alert[];
  /** Recent autopilot messages per drone, newest first. */
  autopilotMessages: Map<DroneId, AutopilotMessage[]>;

  applySnapshot: (drones: DroneSnapshotWire[]) => void;
  applyDelta: (delta: DroneDelta) => void;
  applyDeltas: (deltas: DroneDelta[]) => void;
  upsertDrone: (drone: DroneSnapshotWire) => void;
  removeDrones: (ids: DroneId[]) => void;
  setLink: (status: LinkStatus) => void;
  pushAlert: (alert: Omit<Alert, 'id'>) => void;
  clearAlerts: () => void;
  pushAutopilotMessage: (message: AutopilotMessage) => void;

  select: (id: DroneId) => void;
  toggleSelect: (id: DroneId) => void;
  setSelection: (ids: DroneId[]) => void;
  clearSelection: () => void;

  reset: () => void;
}

let nextAlertId = 1;

/** Deltas for drones not yet in the roster, coalesced per id. Not React state. */
const pending = new Map<DroneId, PendingDelta>();

function mergeScalars(current: DroneScalars, incoming: DroneScalars): DroneScalars {
  return { ...current, ...incoming };
}

/**
 * Merge telemetry per message type.
 *
 * The outer record merges by key, so a delta carrying only ATTITUDE leaves the
 * GLOBAL_POSITION_INT entry alone. The inner record is replaced, matching
 * DroneDelta.merged_with on the server: one message is one complete reading of
 * its type, and half-merging two readings of the same type would produce a
 * record that never came off any aircraft.
 */
function mergeTelemetry(current: TelemetryCache, incoming: TelemetryCache): TelemetryCache {
  if (!Object.keys(incoming).length) return current;
  return { ...current, ...incoming };
}

function fromWire(wire: DroneSnapshotWire): Drone {
  return {
    droneId: wire.droneId,
    callsign: wire.callsign,
    autopilotSysId: wire.autopilotSysId,
    state: wire.state,
    scalars: wire.scalars as unknown as DroneScalars,
    telemetry: wire.telemetry ?? {},
    link: wire.link,
    simulated: wire.simulated,
    lastMessageAt: wire.lastMessageAt,
    // Absent from an older server means unsigned, never "assume signed".
    signing: wire.signing === true,
    access: wire.access === 'map' ? 'map' : 'full',
  };
}

function isMapOnly(drones: Map<DroneId, Drone>, id: DroneId): boolean {
  return drones.get(id)?.access === 'map';
}

function applyToDrone(drone: Drone, delta: Omit<PendingDelta, 'heldSince'>): Drone {
  return {
    ...drone,
    scalars: mergeScalars(drone.scalars, delta.scalars),
    telemetry: mergeTelemetry(drone.telemetry, delta.telemetry),
    lastMessageAt: Math.max(drone.lastMessageAt ?? 0, delta.observedAt),
  };
}

function holdPending(id: DroneId, delta: DroneDelta): void {
  const now = Date.now();
  const existing = pending.get(id);
  if (existing && now - existing.heldSince < PENDING_TTL_MS) {
    pending.set(id, {
      scalars: mergeScalars(existing.scalars, delta.scalars),
      telemetry: mergeTelemetry(existing.telemetry, delta.telemetry),
      observedAt: Math.max(existing.observedAt, delta.observedAt),
      heldSince: existing.heldSince,
    });
    return;
  }
  pending.set(id, {
    scalars: delta.scalars,
    telemetry: delta.telemetry,
    observedAt: delta.observedAt,
    heldSince: now,
  });
}

/** Drain any held delta onto a drone that has just entered the roster. */
function drainPending(id: DroneId, drone: Drone): Drone {
  const held = pending.get(id);
  if (!held) return drone;
  pending.delete(id);
  if (Date.now() - held.heldSince > PENDING_TTL_MS) return drone;
  return applyToDrone(drone, held);
}

function sameIds(a: DroneId[], b: DroneId[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export const useDroneStore = create<DroneStoreState>((set, get) => ({
  drones: new Map(),
  ids: [],
  selectedIds: [],
  link: 'idle',
  alerts: [],
  autopilotMessages: new Map(),

  /**
   * Apply a full roster snapshot.
   *
   * A snapshot sets membership and scalars, but it never replaces a live
   * telemetry cache wholesale. The snapshot is assembled from the same server
   * state, so it can only be equal to or older than what the socket has already
   * delivered; overwriting with it is how the predecessor's 2-second poll
   * clobbered fresher socket data.
   */
  applySnapshot: (wires) => {
    const current = get().drones;
    const next = new Map<DroneId, Drone>();

    for (const wire of wires) {
      const id = wire.droneId;
      const existing = current.get(id);
      const incoming = fromWire(wire);
      if (!existing) {
        next.set(id, drainPending(id, incoming));
        continue;
      }
      if (incoming.access === 'map' && existing.access !== 'map') {
        // The aircraft has just left the caller's team. Merging would keep its
        // battery, mode and raw telemetry on screen for a team that no longer
        // has any right to them.
        next.set(id, incoming);
        continue;
      }
      next.set(id, {
        ...incoming,
        scalars: mergeScalars(existing.scalars, incoming.scalars),
        telemetry: mergeTelemetry(existing.telemetry, incoming.telemetry),
        lastMessageAt: Math.max(existing.lastMessageAt ?? 0, incoming.lastMessageAt ?? 0) || null,
      });
    }

    const ids = [...next.keys()];
    const prevIds = get().ids;
    set({
      drones: next,
      ids: sameIds(ids, prevIds) ? prevIds : ids,
      // An aircraft that has just become another team's leaves the selection,
      // or the next fan-out would still be aimed at it.
      selectedIds: get().selectedIds.filter((id) => next.has(id) && !isMapOnly(next, id)),
    });
  },

  applyDelta: (delta) => {
    get().applyDeltas([delta]);
  },

  applyDeltas: (deltas) => {
    if (!deltas.length) return;
    const current = get().drones;
    let next: Map<DroneId, Drone> | null = null;

    for (const delta of deltas) {
      const id = delta.droneId;
      const drone = (next ?? current).get(id);
      if (!drone) {
        holdPending(id, delta);
        continue;
      }
      next ??= new Map(current);
      next.set(id, applyToDrone(drone, delta));
    }

    if (next) set({ drones: next });
  },

  upsertDrone: (wire) => {
    const current = get().drones;
    const existing = current.get(wire.droneId);
    const incoming = fromWire(wire);
    const merged = existing
      ? {
          ...incoming,
          scalars: mergeScalars(existing.scalars, incoming.scalars),
          telemetry: mergeTelemetry(existing.telemetry, incoming.telemetry),
        }
      : drainPending(wire.droneId, incoming);

    const next = new Map(current);
    next.set(wire.droneId, merged);
    set({ drones: next, ids: existing ? get().ids : [...next.keys()] });
  },

  removeDrones: (ids) => {
    if (!ids.length) return;
    const current = get().drones;
    const next = new Map(current);
    let changed = false;
    const messages = new Map(get().autopilotMessages);
    let messagesChanged = false;
    for (const id of ids) {
      pending.delete(id);
      if (messages.delete(id)) messagesChanged = true;
      if (next.delete(id)) changed = true;
    }
    if (messagesChanged) set({ autopilotMessages: messages });
    if (!changed) return;
    set({
      drones: next,
      ids: [...next.keys()],
      selectedIds: get().selectedIds.filter((id) => next.has(id)),
    });
  },

  setLink: (status) => {
    if (get().link !== status) set({ link: status });
  },

  pushAlert: (alert) => {
    const alerts = [{ ...alert, id: nextAlertId++ }, ...get().alerts];
    set({ alerts: alerts.slice(0, ALERT_LIMIT) });
  },

  clearAlerts: () => set({ alerts: [] }),

  /**
   * Append one autopilot line to its drone's ring, newest first.
   *
   * Only the affected drone's array is replaced, so a panel watching another
   * drone keeps referential equality and does not re-render. Held even for a
   * drone not yet in the roster: the message matters before the snapshot lands.
   */
  pushAutopilotMessage: (message) => {
    const current = get().autopilotMessages;
    const existing = current.get(message.droneId) ?? EMPTY_MESSAGES;
    const next = new Map(current);
    next.set(message.droneId, [message, ...existing].slice(0, MESSAGE_LIMIT));
    set({ autopilotMessages: next });
  },

  // Another team's aircraft cannot be selected. Selection is what every
  // command path reads, so this is the one place that keeps a fan-out, the
  // dock and the context menu from lining up orders the server will refuse.
  select: (id) => {
    if (isMapOnly(get().drones, id)) return;
    set({ selectedIds: [id] });
  },

  toggleSelect: (id) => {
    if (isMapOnly(get().drones, id)) return;
    const selected = get().selectedIds;
    set({
      selectedIds: selected.includes(id)
        ? selected.filter((other) => other !== id)
        : [...selected, id],
    });
  },

  setSelection: (ids) => {
    const drones = get().drones;
    set({ selectedIds: ids.filter((id) => !isMapOnly(drones, id)) });
  },

  clearSelection: () => {
    if (get().selectedIds.length) set({ selectedIds: [] });
  },

  reset: () => {
    pending.clear();
    set({
      drones: new Map(),
      ids: [],
      selectedIds: [],
      link: 'idle',
      alerts: [],
      autopilotMessages: new Map(),
    });
  },
}));

/**
 * Watch one drone without re-rendering React.
 *
 * This is the export the map layer is built on. Marker position, heading and
 * track are driven straight from here into MapLibre, so a 10 Hz fleet costs
 * zero React renders in the position path. The predecessor put every drone into
 * one component's state and then throttled the store to keep that component
 * alive, which is the wrong end of the problem.
 *
 * The callback fires immediately with the current value (or undefined) unless
 * `fireImmediately` is false, and thereafter only when this drone's object
 * identity changes. Returns an unsubscribe function.
 */
export function subscribeToDrone(
  id: DroneId,
  callback: (drone: Drone | undefined) => void,
  options: { fireImmediately?: boolean } = {},
): () => void {
  let previous = useDroneStore.getState().drones.get(id);
  if (options.fireImmediately !== false) callback(previous);

  return useDroneStore.subscribe((state) => {
    const next = state.drones.get(id);
    if (next === previous) return;
    previous = next;
    callback(next);
  });
}

/** Watch roster membership only. Fires when a drone is added or removed. */
export function subscribeToRoster(callback: (ids: DroneId[]) => void): () => void {
  let previous = useDroneStore.getState().ids;
  callback(previous);
  return useDroneStore.subscribe((state) => {
    if (state.ids === previous) return;
    previous = state.ids;
    callback(previous);
  });
}

/**
 * Imperative access for non-React callers (the socket, the map, command code).
 * Reading `useDroneStore.getState()` inline works too, but going through this
 * keeps the call sites searchable.
 */
export const droneActions = {
  applySnapshot: (drones: DroneSnapshotWire[]) => useDroneStore.getState().applySnapshot(drones),
  applyDeltas: (deltas: DroneDelta[]) => useDroneStore.getState().applyDeltas(deltas),
  upsertDrone: (drone: DroneSnapshotWire) => useDroneStore.getState().upsertDrone(drone),
  removeDrones: (ids: DroneId[]) => useDroneStore.getState().removeDrones(ids),
  setLink: (status: LinkStatus) => useDroneStore.getState().setLink(status),
  pushAlert: (alert: Omit<Alert, 'id'>) => useDroneStore.getState().pushAlert(alert),
  pushAutopilotMessage: (message: AutopilotMessage) =>
    useDroneStore.getState().pushAutopilotMessage(message),
  reset: () => useDroneStore.getState().reset(),
  getDrone: (id: DroneId): Drone | undefined => useDroneStore.getState().drones.get(id),
  getIds: (): DroneId[] => useDroneStore.getState().ids,
  /** Ids the client has telemetry for but no roster entry. The socket refetches these. */
  pendingIds: (): DroneId[] => [...pending.keys()],
};

/*
 * Selectors below. Subscribe to the narrowest thing that answers your question.
 * A component that reads the whole store re-renders at the broadcast rate for
 * the entire fleet, which is what made the predecessor need a throttle.
 */

export function useDroneIds(): DroneId[] {
  return useDroneStore((state) => state.ids);
}

export function useDroneCount(): number {
  return useDroneStore((state) => state.ids.length);
}

/**
 * Ids of the caller's own aircraft, leaving out other teams' map-only ones.
 * For pickers that open an aircraft's details: offering another team's would
 * only lead to a screen the server refuses to fill.
 */
export function useOwnDroneIds(): DroneId[] {
  return useDroneStore(
    useShallow((state) => state.ids.filter((id) => state.drones.get(id)?.access !== 'map')),
  );
}

/** How much of this aircraft the caller may see. Absent from the store reads as 'full'. */
export function useDroneAccess(id: DroneId): DroneAccess {
  return useDroneStore((state) => state.drones.get(id)?.access ?? 'full');
}

export function useDrone(id: DroneId | null): Drone | undefined {
  return useDroneStore((state) => (id === null ? undefined : state.drones.get(id)));
}

/** One drone's callsign, state, simulated and signing flags. Ignores telemetry. */
export function useDroneIdentity(id: DroneId): {
  callsign: string;
  state: ConnectionState;
  simulated: boolean;
  signing: boolean;
} | null {
  return useDroneStore(
    useShallow((store) => {
      const drone = store.drones.get(id);
      if (!drone) return null;
      return {
        callsign: drone.callsign,
        state: drone.state,
        simulated: drone.simulated,
        signing: drone.signing,
      };
    }),
  );
}

/**
 * A single scalar. Re-renders only when that one field changes, so a battery
 * readout is not woken by a position fix.
 */
export function useDroneScalar<K extends keyof DroneScalars>(
  id: DroneId,
  key: K,
): DroneScalars[K] | undefined {
  return useDroneStore((state) => state.drones.get(id)?.scalars[key]);
}

/** Several scalars at once, compared shallowly. */
export function useDroneScalars<K extends keyof DroneScalars>(
  id: DroneId,
  keys: readonly K[],
): Pick<DroneScalars, K> {
  return useDroneStore(
    useShallow((state) => {
      const scalars = state.drones.get(id)?.scalars;
      const out: Partial<Pick<DroneScalars, K>> = {};
      if (scalars) {
        for (const key of keys) out[key] = scalars[key];
      }
      return out as Pick<DroneScalars, K>;
    }),
  );
}

/** One entry of the telemetry cache, by MAVLink message type. */
export function useDroneMessage(id: DroneId, msgType: string): Record<string, unknown> | undefined {
  return useDroneStore((state) => state.drones.get(id)?.telemetry[msgType]);
}

export function useSelectedDroneIds(): DroneId[] {
  return useDroneStore((state) => state.selectedIds);
}

export function usePrimarySelectedId(): DroneId | null {
  return useDroneStore((state) => state.selectedIds[0] ?? null);
}

export function useIsSelected(id: DroneId): boolean {
  return useDroneStore((state) => state.selectedIds.includes(id));
}

export function useLinkStatus(): LinkStatus {
  return useDroneStore((state) => state.link);
}

export function useAlerts(): Alert[] {
  return useDroneStore((state) => state.alerts);
}

/** Recent autopilot messages for one drone, newest first. Stable when unchanged. */
export function useAutopilotMessages(id: DroneId): readonly AutopilotMessage[] {
  return useDroneStore((state) => state.autopilotMessages.get(id) ?? EMPTY_MESSAGES);
}

/**
 * The most recent warning-or-worse line for one drone, for the prominent badge.
 *
 * Returns null when the newest lines are all informational, so a PreArm failure
 * surfaces but a routine "using GPS" does not raise an alarm.
 */
export function useLatestAutopilotWarning(id: DroneId): AutopilotMessage | null {
  return useDroneStore((state) => {
    const messages = state.autopilotMessages.get(id);
    if (!messages) return null;
    for (const message of messages) {
      if (message.severity <= WARNING_SEVERITY) return message;
    }
    return null;
  });
}

export function useDroneSelection(): {
  select: (id: DroneId) => void;
  toggleSelect: (id: DroneId) => void;
  setSelection: (ids: DroneId[]) => void;
  clearSelection: () => void;
} {
  return useDroneStore(
    useShallow((state) => ({
      select: state.select,
      toggleSelect: state.toggleSelect,
      setSelection: state.setSelection,
      clearSelection: state.clearSelection,
    })),
  );
}
